/**
 * storedSignature — pure helpers for the CB-3.6 stored-signature surface.
 *
 * Kept out of the component so the default-deny authorization rule and the signature-value logic
 * are unit-testable headlessly. The signature pad itself (canvas/signature_pad) lives in
 * components/contracts/signing/SignaturePad.tsx and consumes the SignatureValue shape defined here.
 */

import type { SaveSignaturePayload } from '@/types/contract';

/** How the signature was produced (CB-5.10, FR-56 audit). Optional + descriptive — only the ceremony
 *  pad sets it; the stored-signature settings surface ignores it. */
export type SignatureMethod = 'typed' | 'drawn' | 'uploaded';

/** One of the three representations is "live": a typed name (+ style) OR an image data-URL. */
export interface SignatureValue {
  signature_image?: string | null;
  signature_typed?: string | null;
  signature_style?: string | null;
  /** typed | drawn | uploaded — captured at adoption for the FR-56 signing-act audit (CB-5.10). */
  signature_method?: SignatureMethod | null;
}

export interface SignatureStyle {
  key: string;
  label: string;
  fontFamily: string;
}

/** Typed-signature faces (UX-DR17). Self-hosted OFL script fonts (@font-face in globals.css); the
 *  SAME font files are registered with reportlab server-side so the sealed PDF matches the picker. */
export const SIGNATURE_STYLES: SignatureStyle[] = [
  { key: 'classic', label: 'Classic', fontFamily: '"Alex Brush", cursive' },
  { key: 'formal', label: 'Formal', fontFamily: '"Great Vibes", cursive' },
  { key: 'casual', label: 'Casual', fontFamily: '"Kalam", cursive' },
];

export const DEFAULT_SIGNATURE_STYLE = SIGNATURE_STYLES[0].key;

/** CSS font-family for a style key, falling back to the first style for an unknown/empty key. */
export function fontForStyle(key: string | null | undefined): string {
  return (SIGNATURE_STYLES.find((s) => s.key === key) ?? SIGNATURE_STYLES[0]).fontFamily;
}

/** A value is a real signature if it has a non-empty typed name or an image. */
export function hasSignature(v: SignatureValue): boolean {
  return Boolean((v.signature_typed && v.signature_typed.trim()) || v.signature_image);
}

/**
 * Build the save payload. DEFAULT-DENY (FR-35): grants are persisted ONLY when the owner has opted
 * in — opting out (or never opting in) sends empty arrays, so no one may apply the signature. We
 * never inject an implicit owner/role grant.
 */
export function buildSignaturePayload(
  value: SignatureValue,
  optIn: boolean,
  allowedRoles: number[],
  allowedUserIds: string[],
): SaveSignaturePayload {
  return {
    signature_image: value.signature_image ?? null,
    signature_typed: value.signature_typed ?? null,
    signature_style: value.signature_style ?? null,
    allowed_roles: optIn ? allowedRoles : [],
    allowed_user_ids: optIn ? allowedUserIds : [],
  };
}
